<template>
  <span v-if="removable" class="chip chip-split" :class="{ 'chip-active': selected }">
    <button type="button" class="chip-apply" :aria-label="buttonLabel" @click="$emit('click', $event)">
      <span v-if="color" class="chip-dot" :style="{ backgroundColor: color }" aria-hidden="true" />
      <slot />
    </button>
    <button type="button" class="chip-remove" :aria-label="removeLabel" @click="$emit('remove')">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
  <button
    v-else
    type="button"
    class="chip"
    :class="{ 'chip-active': selected }"
    :aria-pressed="selected === undefined ? undefined : selected"
    :aria-label="buttonLabel"
    @click="$emit('click', $event)"
  >
    <span v-if="color" class="chip-dot" :style="{ backgroundColor: color }" aria-hidden="true" />
    <slot />
  </button>
</template>

<script setup lang="ts">
/**
 * Pill chip (wraps the global .chip vocabulary) — filter toggles, saved
 * presets, tag pickers. Pass `selected` for the accent-tinted active state
 * (it doubles as aria-pressed on toggle chips); `color` renders the little
 * category dot. `removable` splits the pill into label + ✕ buttons — wire
 * @click (apply) and @remove, and label both for screen readers.
 */
withDefaults(
  defineProps<{
    /** Active/selected tint; also sets aria-pressed on non-removable chips. */
    selected?: boolean
    /** Category dot color rendered before the label. */
    color?: string
    /** Split pill with a ✕ button that emits `remove`. */
    removable?: boolean
    /** aria-label for the main (apply) button. */
    buttonLabel?: string
    /** aria-label for the ✕ button — required with `removable`. */
    removeLabel?: string
  }>(),
  // No boolean default: an undefined `selected` must stay undefined so plain
  // action chips don't get a bogus aria-pressed="false".
  { selected: undefined },
)

defineEmits<{
  (e: "click", event: MouseEvent): void
  (e: "remove"): void
}>()
</script>
